import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { getCities, getCountries, getProvinces } from "apis/location";
import { useAntdMessage } from "hooks/useAntdMessage";
import { useTranslation } from "react-i18next";

export const useCountries = ({ payload = {} }, options = {}) => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["countries", payload],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await getCountries(payload);
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    throwOnError: (error) => {
      return message.error(
        error.message || t("error.failed_to_load_data_from_server")
      );
    },
    ...options,
  });
};
export const useProvinces = ({ payload = {} }, options = {}) => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["provinces", payload],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await getProvinces(payload);
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    throwOnError: (error) => {
      return message.error(
        error.message || t("error.failed_to_load_data_from_server")
      );
    },
    ...options,
  });
};
export const useCities = ({ payload = {} }, options = {}) => {
  const { message } = useAntdMessage();
  const { t } = useTranslation();
  return useQuery({
    queryKey: ["cities", payload],
    queryFn: async () => {
      let response;
      try {
        const {
          data: { data },
        } = await getCities(payload);
        response = data;
      } catch (error) {
        throw new Error(t("error.failed_to_load_data_from_server"));
      }
      return response;
    },
    throwOnError: (error) => {
      return message.error(
        error.message || t("error.failed_to_load_data_from_server")
      );
    },
    ...options,
  });
};

export const useInfiniteCountrySuggestion = (payload, options = {}) => {
  const { message } = useAntdMessage();
  return useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ["infinite/country/suggestion", payload],
    queryFn: async ({ pageParam }) => {
      let response;
      try {
        const {
          data: { data },
        } = await getCountries({
          page: pageParam,
          ...payload,
        });
        response = data;
      } catch (error) {
        throw new Error("Failed to load data from server!");
      }
      return response;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage) {
        return undefined;
      }
      return lastPage.current_page < lastPage.last_page
        ? lastPage.current_page + 1
        : undefined;
    },
    onError: (error) =>
      message.error(
        error?.message ||
          error?.data?.message ||
          "Failed to load data from server!"
      ),
    ...options,
  });
};

export const useInfiniteProvinceSuggestion = (payload, options = {}) => {
  const { message } = useAntdMessage();
  return useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ["infinite/province/suggestion", payload],
    queryFn: async ({ pageParam }) => {
      let response;
      try {
        const {
          data: { data },
        } = await getProvinces({
          page: pageParam,
          ...payload,
        });
        response = data;
      } catch (error) {
        throw new Error("Failed to load data from server!");
      }
      return response;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage) {
        return undefined;
      }
      return lastPage.current_page < lastPage.last_page
        ? lastPage.current_page + 1
        : undefined;
    },
    onError: (error) =>
      message.error(
        error?.message ||
          error?.data?.message ||
          "Failed to load data from server!"
      ),
    ...options,
  });
};

export const useInfiniteCitySuggestion = (payload, options = {}) => {
  const { message } = useAntdMessage();
  return useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ["infinite/city/suggestion", payload],
    queryFn: async ({ pageParam }) => {
      let response;
      try {
        const {
          data: { data },
        } = await getCities({
          page: pageParam,
          ...payload,
        });
        response = data;
      } catch (error) {
        throw new Error("Failed to load data from server!");
      }
      return response;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage) {
        return undefined;
      }
      return lastPage.current_page < lastPage.last_page
        ? lastPage.current_page + 1
        : undefined;
    },
    onError: (error) =>
      message.error(
        error?.message ||
          error?.data?.message ||
          "Failed to load data from server!"
      ),
    ...options,
  });
};
